/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import ModuleWrapper from "@municipio/gatsby-theme-basic/src/components/ModuleWrapper";
import {
  Loading,
  ErrorMessage,
} from "@whitespace/gatsby-theme-smorgasbord/src/components";
import ServerResponseError from "@whitespace/gatsby-theme-smorgasbord/src/ServerResponseError";
import { HTML } from "@whitespace/gatsby-theme-wordpress-basic/src/components";
import { Formik, Field, Form } from "formik";
import { camelCase } from "lodash-es";
import PropTypes from "prop-types";
import { Fragment, useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import apartmentContext from "../../contexts/apartmentContext";

import * as defaultStyles from "./SmorgasbordUserListModule.module.css";

SmorgasbordUserListModule.propTypes = {
  description: PropTypes.string,
  title: PropTypes.node,
  styles: PropTypes.objectOf(PropTypes.string),
};

export default function SmorgasbordUserListModule({
  styles = defaultStyles,
  description,
  title,
  ...restProps
}) {
  const { t } = useTranslation();

  const { selectedApartment } = useContext(apartmentContext);
  const [error, setError] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);
  const [pending, setPending] = useState(true);
  const [users, setUsers] = useState([]);

  useEffect(async () => {
    setPending(true);
    if (!selectedApartment?.id) {
      setError({ code: "NO_SELECTED_APARTMENT" });
    } else if (!selectedApartment.userIsAdmin) {
      setError({ code: "FORBIDDEN" });
    } else {
      try {
        const response = await fetch(
          `/api/apartments/${selectedApartment.id}/users`,
        );
        let result = await response.json();
        if (!response.ok) {
          throw new ServerResponseError(result);
        }
        const { data } = result;
        setUsers(data);
        setError(null);
      } catch (error) {
        console.log(error);
        setError(error);
      }
    }
    setPending(false);
  }, [selectedApartment?.id]);

  if (!selectedApartment?.id) {
    return null;
  }

  if (pending) {
    return <Loading />;
  }

  if (error) {
    return (
      <ErrorMessage
        error={
          t([
            `userListModule.error.${camelCase(error.code || "unknown")}`,
            `userListModule.error.unknown`,
          ]) +
          (process.env.GATSBY_DEBUG_LOGIN && error.code
            ? ` (${error.code})`
            : "")
        }
      />
    );
  }

  const handleDelete = async (ssn) => {
    let user = { ssn };
    const body = JSON.stringify(user);
    try {
      let response = await fetch(
        `/api/apartments/${selectedApartment.id}/users/${ssn}`,
        {
          method: "DELETE",
          body,
          headers: { "Content-Type": "application/json" },
        },
      );
      let result = await response.json();
      if (!response.ok) {
        throw new ServerResponseError(result);
      }
      let { success, data } = result;
      if (success) {
        setUsers(data);
      }
    } catch (err) {
      console.log(
        `Can not delete ssn ${ssn} in SmorgasbordUserListModule:`,
        err.message,
      );
    }
  };

  return (
    <ModuleWrapper title={title} {...restProps}>
      {description && <HTML>{description}</HTML>}
      {pending ? (
        <div>{t([`userList.loading`, `loading`])}</div>
      ) : error ? (
        <div>{t([`userList.error`, `error`])}</div>
      ) : (
        <Fragment>
          {users?.length > 0 && (
            <div className={styles.addedUsersContainer}>
              <h2 className={styles.title}>DINA EXTRA ANVÄNDARE:</h2>
              <ul className={styles.list}>
                {users.map((user) => {
                  return (
                    <li key={user.ssn} className={styles.addedUser}>
                      <span className={styles.ssn}>{user.ssn}</span>
                      <button
                        onClick={() => handleDelete(user.ssn)}
                        className={styles.button}
                      >
                        {t("delete")}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <h2 className={styles.title}>Lägg till användare här:</h2>
          <Formik
            initialValues={{ ssn: "" }}
            onSubmit={async ({ ssn }, actions) => {
              setSubmissionError(null);
              let user = { ssn };
              const body = JSON.stringify(user);
              try {
                let response = await fetch(
                  `/api/apartments/${selectedApartment.id}/users`,
                  {
                    method: "POST",
                    body,
                    headers: { "Content-Type": "application/json" },
                  },
                );
                let result = await response.json();
                if (!response.ok) {
                  throw new ServerResponseError(result);
                }
                let { success, data } = result;
                if (success) {
                  setUsers(data);
                }
                actions.resetForm();
              } catch (err) {
                setSubmissionError(err);
                console.log(
                  `Can not add ssn ${ssn} in SmorgasbordUserListModule:`,
                  err.message,
                );
              }
            }}
          >
            {({ handleSubmit, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                <Field
                  name="ssn"
                  disabled={isSubmitting}
                  className={styles.ssnInput}
                />
                <button type="submit" className={styles.button}>
                  {t("add")}
                </button>
                {submissionError && (
                  <ErrorMessage
                    css={css`
                      margin-top: 1rem;
                    `}
                    error={
                      t([
                        `userListModule.error.${camelCase(
                          submissionError.code || "unknown",
                        )}`,
                        `userListModule.error.unknown`,
                      ]) +
                      (process.env.GATSBY_DEBUG_LOGIN && submissionError.code
                        ? ` (${submissionError.code})`
                        : "")
                    }
                  />
                )}
              </Form>
            )}
          </Formik>
        </Fragment>
      )}
    </ModuleWrapper>
  );
}
